import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconInfo, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { api, ApiError } from "../../../shared/api/client";
import { getUser } from "../../auth/session";

type Invoice = {
  id: number;
  invoice_number: string;
  billing_month: string;
  subscription_fee: number;
  transaction_fees: number;
  total_amount: number;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
};

type Subscription = {
  tenant_name: string;
  subscription_plan: string | null;
  subscription_status: string;
  monthly_fee: number;
  transaction_fee_percentage: number;
  max_users: number;
  max_customers: number;
};

type Usage = {
  users: { current: number; max: number };
  customers: { current: number; max: number };
  user_utilization_pct: number;
  customer_utilization_pct: number;
};

function tenantQuery(user: ReturnType<typeof getUser>): string {
  if (user?.tenant_id) return `?tenant_id=${user.tenant_id}`;
  return "";
}

export function BillingDashboard() {
  const user = getUser();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [subUnavailable, setSubUnavailable] = useState(false);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [markBusy, setMarkBusy] = useState<number | null>(null);

  const load = async () => {
    setPageState("loading");
    setSubUnavailable(false);
    const q = tenantQuery(user);
    try {
      const invRes = await api.get<{ invoices: Invoice[] }>("/billing/invoices");
      setInvoices(invRes.invoices ?? []);

      try {
        const sd = await api.get<Subscription>(`/billing/subscription${q}`);
        setSub(sd);
      } catch (ex) {
        if (ex instanceof ApiError && ex.status === 404) {
          setSub(null);
          setSubUnavailable(true);
        } else {
          throw ex;
        }
      }

      try {
        const ud = await api.get<Usage>(`/billing/usage${q}`);
        setUsage(ud);
      } catch (ex) {
        if (ex instanceof ApiError && ex.status === 404) {
          setUsage(null);
        } else {
          throw ex;
        }
      }

      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load billing data");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (inv: Invoice) => {
    setMarkBusy(inv.id);
    try { await api.post(`/billing/invoices/${inv.id}/mark-paid`, {}); await load(); }
    finally { setMarkBusy(null); }
  };

  const invoiceColumns: ColumnDef<Invoice, unknown>[] = [
    { accessorKey: "invoice_number", header: "Invoice #" },
    { accessorKey: "billing_month", header: "Month" },
    { id: "sub_fee", header: "Subscription", accessorFn: (r) => `${r.currency ?? "TZS"} ${(r.subscription_fee ?? 0).toLocaleString()}` },
    { id: "tx_fees", header: "Transaction Fees", accessorFn: (r) => `${r.currency ?? "TZS"} ${(r.transaction_fees ?? 0).toLocaleString()}` },
    { id: "total", header: "Total", accessorFn: (r) => `${r.currency ?? "TZS"} ${(r.total_amount ?? 0).toLocaleString()}` },
    { id: "status", header: "Status", cell: ({ row }) => (
      <AppBadge tone={row.original.status === "paid" ? "success" : row.original.status === "overdue" ? "error" : "warning"}>
        {row.original.status === "paid" ? <IconCheck size={12}/> : <IconWarning size={12}/>}
        {row.original.status.toUpperCase()}
      </AppBadge>
    )},
    { id: "due_date", header: "Due", accessorFn: (r) => r.due_date ? new Date(r.due_date).toLocaleDateString("en-GB") : "—" },
    { id: "actions", header: "", enableSorting: false, cell: ({ row }) => (
      row.original.status !== "paid"
        ? <AppButton type="button" variant="secondary" disabled={markBusy === row.original.id}
            onClick={(e) => { e.stopPropagation(); markPaid(row.original); }}>
            {markBusy === row.original.id ? <IconSpinner size={12}/> : "Mark Paid"}
          </AppButton>
        : null
    )},
  ];

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle="Subscription, usage, and invoices."
      />

      <PageState status={pageState} error={pageError} onRetry={load}>
        {subUnavailable && (
          <AppCard style={{ marginBottom: 16 }}>
            <div className="pg-state--inline"><IconInfo size={28}/><p>Select a tenant to view subscription details.</p></div>
          </AppCard>
        )}

        {sub && (
          <div className="pg-stats-row">
            <AppCard>
              <div className="pg-stat-label">Plan</div>
              <div className="pg-stat-value">{sub.subscription_plan ?? "Custom"}</div>
              <AppBadge tone={sub.subscription_status === "active" ? "success" : "warning"}>{(sub.subscription_status ?? "unknown").toUpperCase()}</AppBadge>
            </AppCard>
            <AppCard>
              <div className="pg-stat-label">Monthly Fee</div>
              <div className="pg-stat-value">TZS {(sub.monthly_fee ?? 0).toLocaleString()}</div>
              <div className="pg-text-muted">{sub.transaction_fee_percentage ?? 0}% per transaction</div>
            </AppCard>
            {usage && (
              <>
                <AppCard>
                  <div className="pg-stat-label">Users</div>
                  <div className="pg-stat-value">{usage.users.current} / {usage.users.max ?? "∞"}</div>
                  <div className="pg-progress-bar"><div className="pg-progress-fill" style={{ width: `${usage.user_utilization_pct ?? 0}%` }} /></div>
                </AppCard>
                <AppCard>
                  <div className="pg-stat-label">Customers</div>
                  <div className="pg-stat-value">{usage.customers.current} / {usage.customers.max ?? "∞"}</div>
                  <div className="pg-progress-bar"><div className="pg-progress-fill" style={{ width: `${usage.customer_utilization_pct ?? 0}%` }} /></div>
                </AppCard>
              </>
            )}
          </div>
        )}

        <AppCard title="Invoices" style={{ marginTop: 16 }}>
          {invoices.length === 0 ? (
            <PageState status="empty" emptyTitle="No invoices" emptyHint="Invoices will appear here once generated." />
          ) : (
            <AppDataTable data={invoices} columns={invoiceColumns} />
          )}
        </AppCard>
      </PageState>
    </>
  );
}
